import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, ShieldCheck, Coins, Lock, Sparkles, Info, BadgeCheck, X, Bell,
} from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { AuraRing } from "@/components/brand/AuraRing";
import { useAura } from "@/context/AuraContext";
import { CURRENT_USER } from "@/data/mock";
import { cn } from "@/lib/utils";

const STEPS = ["Concept", "Identity", "Coin", "Economics", "Review", "Status"];

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-1.5">
      {STEPS.map((s, i) => (
        <span
          key={s}
          className={cn(
            "h-1.5 rounded-full transition-all duration-300",
            i < step ? "w-6 aura-gradient" : i === step ? "w-6 bg-white" : "w-1.5 bg-am-surface3"
          )}
        />
      ))}
    </div>
  );
}

export default function PersonalCoinLaunch() {
  const navigate = useNavigate();
  const { profile } = useAura();
  const [step, setStep] = useState(0);

  const displayName = profile.name || CURRENT_USER.name;
  const handle = profile.username || CURRENT_USER.username;
  const avatar = profile.avatar || CURRENT_USER.avatar;

  const [coinName, setCoinName] = useState("");
  const [ticker, setTicker] = useState(handle.slice(0, 6).toUpperCase());
  const [desc, setDesc] = useState("");

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => (step === 0 ? navigate(-1) : setStep((s) => s - 1));

  const finalName = coinName || `${displayName}`;
  const finalTicker = "$" + (ticker || handle.slice(0, 6).toUpperCase());

  return (
    <div className="relative min-h-screen pb-24" data-testid="launch-page">
      {/* radiant backdrop */}
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[420px] -translate-x-1/2 aura-radial opacity-[0.14] blur-3xl" />

      {/* top bar */}
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-am-border bg-am-bg/80 px-4 py-3 backdrop-blur-md md:px-8">
        <button onClick={back} data-testid="launch-back" className="grid h-9 w-9 place-items-center rounded-full text-am-text2 hover:bg-white/5 hover:text-white">
          {step === 0 ? <X className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
        </button>
        <StepDots step={step} />
        <button onClick={() => navigate("/u/me")} data-testid="launch-exit" className="text-sm font-medium text-am-text2 hover:text-white">Exit</button>
      </div>

      <div className="mx-auto max-w-xl px-4 py-8 md:px-6 md:py-12">
        {/* prototype banner */}
        <div className="mb-8 flex items-center justify-center gap-2 rounded-full border border-aura-gold/25 bg-aura-gold/[0.06] px-4 py-2 text-xs font-semibold text-aura-gold">
          <Info className="h-3.5 w-3.5" /> Prototype walkthrough — no coin is created
        </div>

        {/* STEP 0 — concept */}
        {step === 0 && (
          <div className="text-center">
            <div className="mx-auto mb-6 h-20 w-20"><Logo size={80} /></div>
            <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-white">
              One person.<br />One identity.<br /><span className="aura-text">One official coin.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-md text-[15px] leading-relaxed text-am-text2">
              Every AuraMax identity will eventually be able to launch a single official personal coin tied directly to their profile. This is a preview of how that journey will feel.
            </p>
            <div className="mt-8 grid gap-3 text-left">
              {[
                { icon: ShieldCheck, t: "Verified identity first", d: "Coins are anchored to a real, verified person or brand." },
                { icon: Coins, t: "One coin per account", d: "No duplicates. Your coin is uniquely yours, forever." },
                { icon: Sparkles, t: "Powered by your Aura", d: "Your community and momentum drive your coin's story." },
              ].map((f) => (
                <div key={f.t} className="flex items-start gap-3 rounded-2xl border border-am-border bg-am-surface/50 p-4">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl aura-gradient"><f.icon className="h-5 w-5 text-black" /></span>
                  <div>
                    <div className="text-sm font-bold text-white">{f.t}</div>
                    <div className="text-sm text-am-text2">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 1 — identity */}
        {step === 1 && (
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white">Verify your identity</h2>
            <p className="mt-2 text-sm leading-relaxed text-am-text2">
              A verified identity confirms a profile truly represents the person, creator, business or organization it claims to be. Only verified identities become eligible to launch a coin.
            </p>
            <div className="mt-6 flex items-center gap-4 rounded-2xl border border-am-border bg-am-surface/60 p-4">
              <AuraRing src={avatar} size={54} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold text-white">{displayName}</div>
                <div className="truncate text-xs text-am-text2">@{handle}</div>
              </div>
              <span className="flex items-center gap-1.5 rounded-full border border-am-border-strong px-3 py-1.5 text-xs font-semibold text-am-text2">
                <Lock className="h-3.5 w-3.5" /> Not verified
              </span>
            </div>
            <div className="mt-4 grid gap-2">
              {["Government ID or business docs", "Selfie / liveness check", "Match to your AuraMax profile"].map((r) => (
                <div key={r} className="flex items-center gap-3 rounded-xl border border-am-border bg-am-surface/40 px-4 py-3 text-sm text-am-text2">
                  <BadgeCheck className="h-4 w-4 shrink-0 text-am-text3" /> {r}
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-am-border bg-am-surface2 px-4 py-3 text-sm text-am-text2">
              <Info className="h-4 w-4 shrink-0 text-aura-gold" /> Real identity verification (KYC) is not enabled in this prototype.
            </div>
          </div>
        )}

        {/* STEP 2 — coin identity */}
        {step === 2 && (
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white">Design your coin</h2>
            <p className="mt-2 text-sm text-am-text2">This is how your official personal coin would show up across AuraMax.</p>

            <div className="mt-6 flex items-center gap-4">
              <div className="relative">
                <AuraRing src={avatar} size={72} ring={3} />
                <span className="absolute -bottom-1 -right-1 grid h-7 w-7 place-items-center rounded-full border-2 border-am-bg bg-am-surface3">
                  <Coins className="h-3.5 w-3.5 aura-text" />
                </span>
              </div>
              <p className="text-sm text-am-text2">Your coin image uses your Aura profile mark by default.</p>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-white">Coin name</label>
                <input
                  value={coinName} onChange={(e) => setCoinName(e.target.value)} maxLength={24}
                  placeholder={`${displayName} Coin`} data-testid="launch-coin-name"
                  className="w-full rounded-xl border border-am-border bg-am-surface px-4 py-3 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-white">Ticker</label>
                <div className="flex items-center rounded-xl border border-am-border bg-am-surface px-4 focus-within:border-am-border-strong">
                  <span className="text-sm font-bold aura-text">$</span>
                  <input
                    value={ticker} onChange={(e) => setTicker(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 8))}
                    placeholder="TICKER" data-testid="launch-ticker"
                    className="w-full bg-transparent py-3 pl-1 text-sm font-semibold text-white placeholder:text-am-text3 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-white">About your coin</label>
                <textarea
                  value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} maxLength={160}
                  placeholder="What is your coin about? Tell your community…" data-testid="launch-desc"
                  className="w-full resize-none rounded-xl border border-am-border bg-am-surface px-4 py-3 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — economics */}
        {step === 3 && (
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white">Economics</h2>
            <p className="mt-2 text-sm leading-relaxed text-am-text2">
              A coin's supply, allocation and trading rules are configured at launch. These are not finalized in the AuraMax prototype, so no values are set here.
            </p>
            <div className="mt-6 grid gap-2">
              {["Total supply", "Creator allocation", "Community allocation", "Trading fees"].map((k) => (
                <div key={k} className="flex items-center justify-between rounded-xl border border-am-border bg-am-surface/50 px-4 py-3.5">
                  <span className="text-sm font-medium text-white">{k}</span>
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-am-text3"><Lock className="h-3.5 w-3.5" /> Set at launch</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-am-border bg-am-surface2 px-4 py-3 text-sm text-am-text2">
              <Info className="h-4 w-4 shrink-0 text-aura-gold" /> No tokenomics, returns or launch dates are promised in this prototype.
            </div>
          </div>
        )}

        {/* STEP 4 — review */}
        {step === 4 && (
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white">Preview your coin</h2>
            <p className="mt-2 text-sm text-am-text2">Here's how it would look — this is a preview only.</p>

            <div className="relative mt-6 overflow-hidden rounded-3xl border border-am-border bg-gradient-to-b from-am-surface/70 to-am-bg p-5">
              <span className="absolute right-4 top-4 rounded-full border border-aura-gold/30 bg-aura-gold/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-aura-gold">
                Preview · Not launched
              </span>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <AuraRing src={avatar} size={64} ring={3} />
                  <span className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full border-2 border-am-bg bg-am-surface3"><Coins className="h-3 w-3 aura-text" /></span>
                </div>
                <div className="min-w-0">
                  <div className="truncate font-display text-lg font-extrabold text-white">{finalName}</div>
                  <div className="text-sm font-semibold aura-text">{finalTicker}</div>
                </div>
              </div>
              {desc && <p className="mt-4 text-sm leading-relaxed text-am-text2">{desc}</p>}
              <div className="mt-5 grid grid-cols-3 gap-3">
                {["Price", "Market cap", "Holders"].map((k) => (
                  <div key={k} className="rounded-xl border border-am-border bg-am-surface/50 px-3 py-2.5 text-center">
                    <div className="text-sm font-bold text-am-text3">—</div>
                    <div className="text-[11px] text-am-text3">{k}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl border border-am-border bg-am-bg/60 px-3 py-2.5 text-xs text-am-text2">
                <ShieldCheck className="h-4 w-4 shrink-0 text-aura-mint" /> Anchored to @{handle} · one coin per identity
              </div>
            </div>
          </div>
        )}

        {/* STEP 5 — status */}
        {step === 5 && (
          <div className="text-center">
            <div className="mx-auto mb-6 grid h-20 w-20 place-items-center rounded-3xl aura-gradient">
              <Coins className="h-9 w-9 text-black" />
            </div>
            <h2 className="font-display text-2xl font-extrabold text-white">You're on the list.</h2>
            <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-am-text2">
              Personal Coins are <span className="text-white">not enabled in the AuraMax prototype</span>. Nothing was created or launched. When coins go live, verified identities will be first in line.
            </p>
            <div className="mt-6 rounded-2xl border border-am-border bg-am-surface/50 p-4 text-left">
              <div className="flex items-center justify-between">
                <span className="text-sm text-am-text2">Coin status</span>
                <span className="rounded-full border border-am-border-strong px-3 py-1 text-xs font-semibold text-am-text2">Not launched</span>
              </div>
            </div>
            <button
              onClick={() => toast("We'll notify you when Personal Coins go live.", { description: "Prototype — no coin was created." })}
              data-testid="launch-notify"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-am-border-strong py-3 text-sm font-semibold text-white hover:bg-white/5"
            >
              <Bell className="h-4 w-4" /> Notify me at launch
            </button>
          </div>
        )}

        {/* footer actions */}
        <div className="mt-10">
          {step < 5 ? (
            <button
              onClick={next}
              data-testid="launch-next"
              className="flex w-full items-center justify-center gap-2 rounded-full p-[1.5px] aura-gradient transition-transform hover:scale-[1.01] active:scale-95"
            >
              <span className="flex w-full items-center justify-center gap-2 rounded-full bg-am-bg py-3 text-sm font-bold text-white">
                {step === 0 ? "Preview the launch flow" : step === 4 ? "Looks good" : "Continue"}
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          ) : (
            <button
              onClick={() => navigate("/u/me")}
              data-testid="launch-done"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-black transition-colors hover:bg-white/90"
            >
              <Check className="h-4 w-4" /> Back to profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
